import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Forward10Icon from "@mui/icons-material/Forward10";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Replay10Icon from "@mui/icons-material/Replay10";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import WaterfallChartIcon from "@mui/icons-material/WaterfallChart";
import { api, unwrap } from "../api/client";
import {
  canChooseOutput,
  chooseOutput,
  DEFAULT_MIX,
  outputDevices,
  type SourceMix,
} from "../audio/mixer";
import { planSources, unplayable } from "../playback/plan";
import { PlayLog } from "../playback/playlog";
import {
  PlaybackSession,
  playsWorkingCopies,
  type FullState,
  RATES,
  type SessionSource,
} from "../playback/session";
import { audibleAt } from "../playback/scheduled-audio";
import { AgcControls } from "./AgcControls";
import { LevelMeter, Spectrogram, Spectrum } from "./AudioMeters";
import { UI_INTERVALS_MS, UiFrames, UiFramesContext } from "./uiFrames";
import { VideoGrid } from "./VideoGrid";

const DAY = 86_400_000;
/** Fetch the next day's recordings this long before the loaded range ends. */
const EXTEND_BEFORE_MS = 3_600_000;
const OUTPUT_KEY = "avgv.audioOutput";
const SPECTROGRAM_KEY = "avgv.spectrogram";

function stored(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Remembered settings are a convenience.
  }
}

const dayOf = (utcMs: number) => Math.floor(utcMs / DAY) * DAY;

export interface SeekRequest {
  utcMs: number;
  /** Changes on every request, so seeking to the same time twice works. */
  n: number;
  /** Start playing from there, rather than keep playing or paused. */
  play?: boolean;
  /** Pause there instead. */
  pause?: boolean;
}

interface Status {
  now: number;
  playing: boolean;
  held: boolean;
  rate: number;
  active: ReadonlySet<string>;
  errors: string[];
  agcGainDb: number | null;
  full: FullState | null;
}

/**
 * Plays every source from the UTC day around the playhead onwards, kept in
 * sync (design §7.2); later days are added as the playhead nears them.
 * Space plays or pauses, arrows step 5 s (Shift: 1 min), [ and ] change speed.
 */
/** Played spans are reported at least this often while playing. */
const REPORT_MS = 60_000;

function report(log: PlayLog) {
  const spans = log.take();
  if (spans.length)
    void api.POST("/api/files/played", { body: { spans } }).catch(() => undefined);
}

function lastEnd(session: PlaybackSession): number {
  let end = -Infinity;
  for (const s of session.sources)
    for (const f of [...s.plan.video, ...s.plan.audio]) end = Math.max(end, f.toUtcMs);
  return end;
}

export function Player({
  seek,
  filter,
  onTime,
  onPlaying,
  onClose,
  map,
}: {
  seek: SeekRequest;
  filter: { library?: string; case?: string };
  onTime: (utcMs: number) => void;
  /** Called when playback starts or stops. */
  onPlaying?: (playing: boolean) => void;
  onClose: () => void;
  /** Offered in the grid's slots, beside the spectrogram. */
  map?: React.ReactNode;
}) {
  const [day, setDay] = useState(() => dayOf(seek.utcMs));
  /** End of the recordings the session has. */
  const loadedTo = useRef(day + DAY);
  const extending = useRef(false);
  const [session, setSession] = useState<PlaybackSession | null>(null);
  const [status, setStatus] = useState<Status | null>(null);
  const position = useRef(seek.utcMs);
  const wasPlaying = useRef(seek.play ?? false);
  const [mixes, setMixes] = useState<Record<string, SourceMix>>({});
  const [outputs, setOutputs] = useState<{ id: string; label: string }[]>([]);
  const [output, setOutput] = useState(() => stored(OUTPUT_KEY));
  const onTimeRef = useRef(onTime);
  const onPlayingRef = useRef(onPlaying);
  useEffect(() => {
    onTimeRef.current = onTime;
    onPlayingRef.current = onPlaying;
  });

  const fetchDay = (from: number) =>
    unwrap(
      api.GET("/api/timeline", {
        params: {
          query: {
            from: new Date(from).toISOString(),
            to: new Date(from + DAY).toISOString(),
            ...filter,
          },
        },
      }),
    );
  const lanes = useQuery({
    queryKey: ["timeline", "player", day, filter.library, filter.case],
    queryFn: () => fetchDay(day),
  });
  const fetchDayRef = useRef(fetchDay);
  useEffect(() => {
    fetchDayRef.current = fetchDay;
  });

  useEffect(() => {
    if (!lanes.data) return;
    let cancelled = false;
    let built: PlaybackSession | null = null;
    loadedTo.current = day + DAY;
    extending.current = false;
    void PlaybackSession.create(
      planSources(lanes.data.lanes),
      position.current,
    ).then((s) => {
      if (cancelled) {
        s.destroy();
        return;
      }
      built = s;
      if (wasPlaying.current) s.play();
      setSession(s);
    });
    return () => {
      cancelled = true;
      built?.destroy();
      setSession(null);
    };
  }, [lanes.data, day]);

  useEffect(() => {
    if (!session) return;
    for (const [key, mix] of Object.entries(mixes)) session.mixer.set(key, mix);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- later changes go through setMix
  }, [session]);

  useEffect(() => {
    if (!session || !canChooseOutput(session.ctx)) return;
    chooseOutput(session.ctx, output).catch(() => setOutput(""));
  }, [session, output]);

  useEffect(() => {
    const list = () =>
      void outputDevices().then(setOutputs, () => setOutputs([]));
    list();
    navigator.mediaDevices?.addEventListener("devicechange", list);
    return () =>
      navigator.mediaDevices?.removeEventListener("devicechange", list);
  }, []);

  const setMix = (key: string, change: Partial<SourceMix>) => {
    setMixes((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? DEFAULT_MIX), ...change },
    }));
    session?.mixer.set(key, change);
  };
  const pickOutput = (id: string) => {
    setOutput(id);
    store(OUTPUT_KEY, id);
  };
  const [spectrogram, setSpectrogram] = useState(
    () => stored(SPECTROGRAM_KEY) === "on",
  );
  const toggleSpectrogram = (on: boolean) => {
    setSpectrogram(on);
    store(SPECTROGRAM_KEY, on ? "on" : "");
  };
  const showOnly = useCallback(
    (keys: ReadonlySet<string>) => session?.showOnly(keys),
    [session],
  );
  const [frames] = useState(() => new UiFrames());
  /** Index into UI_INTERVALS_MS, raised by the grid while videos drop frames. */
  const [uiStep, setUiStep] = useState(0);
  const uiInterval = useRef<number>(UI_INTERVALS_MS[0]);
  useEffect(() => {
    uiInterval.current = UI_INTERVALS_MS[uiStep] ?? UI_INTERVALS_MS[0];
  }, [uiStep]);
  const setFull = useCallback(
    (key: string | null) => session?.setFullResolution(key),
    [session],
  );
  const soloing = Object.values(mixes).some((m) => m.solo);
  const audioControls = (s: SessionSource) => {
    if (s.plan.audio.length === 0) return null;
    const mix = mixes[s.plan.key] ?? DEFAULT_MIX;
    return (
      <AudioControls
        name={s.plan.source}
        meter={session?.mixer.meter(s.plan.key) ?? null}
        mix={mix}
        audible={!mix.muted && (!soloing || mix.solo)}
        onChange={(change) => setMix(s.plan.key, change)}
      />
    );
  };

  useEffect(() => {
    position.current = seek.utcMs;
    if (seek.play) wasPlaying.current = true;
    if (seek.pause) wasPlaying.current = false;
    if (seek.utcMs < day || seek.utcMs >= loadedTo.current)
      setDay(dayOf(seek.utcMs));
    else {
      if (seek.pause) session?.pause();
      else session?.seek(seek.utcMs);
      if (seek.play) session?.play();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on a new request
  }, [seek.n]);

  useEffect(() => {
    if (!session) return;
    let frame = 0;
    let lastUi = 0;
    let reported: boolean | null = null;
    const log = new PlayLog();
    let lastReport = performance.now();
    const files = () =>
      session.sources.flatMap((s) => [...s.plan.video, ...s.plan.audio]);
    const loop = (t: number) => {
      frame = requestAnimationFrame(loop);
      session.tick();
      // Past the last recording loaded: stop there rather than play on into nothing.
      const end = lastEnd(session);
      if (session.isPlaying && Number.isFinite(end) && session.now() >= end) {
        session.pause();
        session.seek(end);
      }
      const now = session.now();
      log.update(now, session.isPlaying, files());
      if (t - lastReport > REPORT_MS || (!session.isPlaying && reported)) {
        lastReport = t;
        report(log);
      }
      position.current = now;
      wasPlaying.current = session.isPlaying;
      if (now < day || now >= loadedTo.current + DAY) setDay(dayOf(now));
      else if (
        now >= loadedTo.current - EXTEND_BEFORE_MS &&
        !extending.current
      ) {
        extending.current = true;
        const from = loadedTo.current;
        void fetchDayRef
          .current(from)
          .then((next) => session.extend(planSources(next.lanes)))
          .then(() => {
            loadedTo.current = from + DAY;
            extending.current = false;
          })
          .catch(() =>
            setTimeout(() => {
              extending.current = false;
            }, 10_000),
          );
      }
      if (t - lastUi < uiInterval.current) return;
      const dt = t - lastUi;
      lastUi = t;
      if (session.isPlaying !== reported) {
        reported = session.isPlaying;
        onPlayingRef.current?.(reported);
      }
      flushSync(() => {
        onTimeRef.current(now);
        setStatus({
          now,
          playing: session.isPlaying,
          held: session.isHeld,
          rate: session.rate,
          active: new Set(
            session.sources
              .filter((s) => session.isActive(s))
              .map((s) => s.plan.key),
          ),
          errors: session.errors(),
          agcGainDb: session.agcGainDb,
          full: session.fullResolution,
        });
      });
      frames.run(t, dt);
    };
    frame = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(frame);
      log.update(session.now(), false, files());
      report(log);
    };
  }, [session, day, frames]);

  const toggle = () => {
    if (!session) return;
    if (session.isPlaying) session.pause();
    else session.play();
  };
  const step = (ms: number) => session?.seek(session.now() + ms);
  const changeRate = (dir: 1 | -1) => {
    if (!session) return;
    const i = RATES.indexOf(session.rate as (typeof RATES)[number]);
    const next = RATES[Math.min(Math.max(i + dir, 0), RATES.length - 1)];
    if (next !== undefined) session.setRate(next);
  };

  const keys = useRef({ toggle, step, changeRate });
  useEffect(() => {
    keys.current = { toggle, step, changeRate };
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (
        el?.closest(
          "input, textarea, select, [role=combobox], [contenteditable=true]",
        )
      )
        return;
      const k = keys.current;
      if (e.key === " " || e.key === "k") k.toggle();
      else if (e.key === "ArrowLeft") k.step(e.shiftKey ? -60_000 : -5_000);
      else if (e.key === "ArrowRight") k.step(e.shiftKey ? 60_000 : 5_000);
      else if (e.key === "[") k.changeRate(-1);
      else if (e.key === "]") k.changeRate(1);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const videoSources = session?.sources.filter((s) => s.chain) ?? [];
  const audioOnly = session?.sources.filter((s) => !s.chain) ?? [];
  const now = status?.now ?? seek.utcMs;

  return (
    <UiFramesContext.Provider value={frames}>
      <Paper
        variant="outlined"
        sx={{ p: 2 }}
        component="section"
        aria-label="Player"
        data-ui-interval={UI_INTERVALS_MS[uiStep]}
      >
        <Stack direction="row" sx={{ alignItems: "flex-start", gap: 1, mb: 1 }}>
          <Stack
            direction="row"
            sx={{ alignItems: "center", gap: 1, flexWrap: "wrap", flexGrow: 1 }}
          >
            <Tooltip title={status?.playing ? "Pause (Space)" : "Play (Space)"}>
              <span>
                <IconButton
                  onClick={toggle}
                  disabled={!session}
                  aria-label={status?.playing ? "Pause" : "Play"}
                >
                  {status?.playing ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton
              onClick={() => step(-10_000)}
              disabled={!session}
              aria-label="Back 10 seconds"
            >
              <Replay10Icon />
            </IconButton>
            <IconButton
              onClick={() => step(10_000)}
              disabled={!session}
              aria-label="Forward 10 seconds"
            >
              <Forward10Icon />
            </IconButton>
            <Select
              size="small"
              value={status?.rate ?? 1}
              onChange={(e) => session?.setRate(Number(e.target.value))}
              inputProps={{ "aria-label": "Speed" }}
              disabled={!session}
            >
              {RATES.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}×
                </MenuItem>
              ))}
            </Select>
            <Typography
              sx={{ fontFamily: "monospace", ml: 1 }}
              aria-label="Playhead (UTC)"
              aria-live="off"
            >
              {new Date(now).toISOString().replace("T", " ").slice(0, 21)} UTC
            </Typography>
            {status && !audibleAt(status.rate) && (
              <Chip size="small" label="Audio muted at this speed" />
            )}
            {status?.held && (
              <Chip size="small" color="warning" label="Buffering…" />
            )}
            <Box sx={{ flexGrow: 1 }} />
            <AgcControls session={session} gainDb={status?.agcGainDb ?? null} />
            {session && <Spectrum analyser={session.mixer.masterMeter} />}
            {session && (
              <Tooltip title={spectrogram ? "Hide spectrogram" : "Spectrogram"}>
                <IconButton
                  aria-label="Spectrogram"
                  aria-pressed={spectrogram}
                  onClick={() => toggleSpectrogram(!spectrogram)}
                >
                  <WaterfallChartIcon
                    color={spectrogram ? "primary" : undefined}
                  />
                </IconButton>
              </Tooltip>
            )}
            {session && canChooseOutput(session.ctx) && outputs.length > 0 && (
              <Select
                size="small"
                value={outputs.some((o) => o.id === output) ? output : ""}
                onChange={(e) => pickOutput(e.target.value)}
                displayEmpty
                inputProps={{ "aria-label": "Audio output" }}
              >
                <MenuItem value="">Default output</MenuItem>
                {outputs.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Stack>
          <IconButton onClick={onClose} aria-label="Close player">
            <CloseIcon />
          </IconButton>
        </Stack>
        {session && spectrogram && (
          <Box sx={{ mb: 1 }}>
            <Spectrogram
              analyser={session.mixer.masterMeter}
              playing={status?.playing ?? false}
            />
          </Box>
        )}
        {!playsWorkingCopies() && (
          <Alert severity="error" sx={{ mb: 1 }}>
            This browser can&apos;t play H.264 video or AAC sound (VS
            Code&apos;s built-in browser is one). Use Chrome, Edge or Safari, or
            the desktop app.
          </Alert>
        )}
        {status?.errors.map((e) => (
          <Alert key={e} severity="error" sx={{ mb: 1 }}>
            {e}
          </Alert>
        ))}
        {lanes.isSuccess && session && session.sources.length === 0 && (
          <Typography color="text.secondary">
            No playable recordings on this day.
          </Typography>
        )}
        {lanes.isSuccess && unplayable(lanes.data.lanes).length > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Not playable yet:{" "}
            {unplayable(lanes.data.lanes)
              .map((u) => `${u.source} (${u.reason})`)
              .join("; ")}
            .
          </Alert>
        )}
        <VideoGrid
          sources={videoSources}
          active={status?.active ?? new Set()}
          now={now}
          playing={status?.playing ?? false}
          onShow={showOnly}
          controls={audioControls}
          full={status?.full ?? null}
          onFull={setFull}
          uiStep={uiStep}
          onUiStep={setUiStep}
          panels={[
            ...(session
              ? [
                  {
                    key: "panel:spectrogram",
                    title: "Spectrogram",
                    node: (
                      <Spectrogram
                        analyser={session.mixer.masterMeter}
                        playing={status?.playing ?? false}
                      />
                    ),
                  },
                ]
              : []),
            ...(map ? [{ key: "panel:map", title: "Map", node: map }] : []),
          ]}
        />
        {audioOnly.length > 0 && (
          <Stack sx={{ gap: 0.5, mt: 1 }}>
            {audioOnly.map((s) => (
              <Stack
                key={s.plan.key}
                direction="row"
                sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}
              >
                <Typography variant="body2" sx={{ minWidth: 160 }}>
                  {s.plan.source}{" "}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    {status?.active.has(s.plan.key) ? "audio" : "no recording"}
                  </Typography>
                </Typography>
                {audioControls(s)}
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>
    </UiFramesContext.Provider>
  );
}

function AudioControls({
  name,
  meter,
  mix,
  audible,
  onChange,
}: {
  name: string;
  meter: AnalyserNode | null;
  mix: SourceMix;
  audible: boolean;
  onChange: (change: Partial<SourceMix>) => void;
}) {
  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 1, ml: "auto" }}>
      {meter && <LevelMeter analyser={meter} name={name} />}
      <IconButton
        size="small"
        aria-label={`Mute ${name}`}
        aria-pressed={mix.muted}
        onClick={() => onChange({ muted: !mix.muted })}
      >
        {audible ? (
          <VolumeUpIcon fontSize="small" />
        ) : (
          <VolumeOffIcon
            fontSize="small"
            color={mix.muted ? "error" : "disabled"}
          />
        )}
      </IconButton>
      <ToggleButton
        value="solo"
        size="small"
        selected={mix.solo}
        onChange={() => onChange({ solo: !mix.solo })}
        aria-label={`Solo ${name}`}
        sx={{ py: 0, px: 1 }}
      >
        S
      </ToggleButton>
      <Slider
        size="small"
        min={0}
        max={2}
        step={0.05}
        value={mix.volume}
        onChange={(_, v) => onChange({ volume: v as number })}
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
        aria-label={`Volume ${name}`}
        sx={{ width: 100 }}
      />
    </Stack>
  );
}
